/**
 * Products Service
 *
 * Fetches product data from Liquid JSON endpoints.
 * Follows the existing pattern from page-map-v4.ts where products
 * are fetched from /collections/all?view=preorder-products.json
 */

import type { ShopifyProduct } from '@/types/shopify';
import { LocationType } from '@/constants/map';

export interface ProductsResponse {
  cart_items: number[];
  items: ShopifyProduct[];
  items_array: number[];
  page: number;
  next_page: string | false;
}

/**
 * Fetch pre-order products from Liquid JSON endpoint
 *
 * @param locationType - Type of location (Nashville vs other)
 * @returns Products data
 */
export async function fetchPreorderProducts(
  locationType: string = LocationType.TOUR
): Promise<ProductsResponse> {
  const endpoint =
    locationType === LocationType.NASHVILLE
      ? '/collections/all?view=nashville-preorder-products.json'
      : '/collections/all?view=preorder-products.json';

  try {
    const response = await fetch(endpoint);

    if (!response.ok) {
      throw new Error(`Failed to fetch products: ${response.statusText}`);
    }

    const data = await response.json();
    return data;
  } catch (error) {
    console.error('[ProductsService] Error fetching products:', error);
    throw error;
  }
}

/**
 * Filter products by location product IDs and tags
 *
 * Matches the filtering logic from page-map-v4.ts lines 1171-1193
 *
 * @param allProducts - All pre-order products from API
 * @param productIds - Product IDs available at this location
 * @param currentMarketId - Current market ID for tag filtering
 * @param excludableLocationIds - Location IDs that exclude certain products
 * @param currentLocationId - Current location ID
 * @returns Filtered and sorted products
 */
export function filterLocationProducts(
  allProducts: ShopifyProduct[],
  productIds: string[],
  _currentMarketId?: string,
  excludableLocationIds: number[] = [
    2290, 2289, 2297, 2319, 2286, 2391, 2400, 2285, 2261, 2321, 2292, 2323,
    2306, 2322, 2287, 717, 2441, 2307,
  ],
  currentLocationId?: number
): ShopifyProduct[] {
  // Apply exclusion filter if location is in the excludable list
  let filteredProductIds = [...productIds];
  if (currentLocationId && excludableLocationIds.includes(currentLocationId)) {
    filteredProductIds = filteredProductIds.filter(
      (id) => id !== '10213098389668'
    );
  }

  // Filter products
  const unorderedProducts: Record<string, ShopifyProduct> = {};

  allProducts.forEach((product, index) => {
    // Must have 'has_active_category' tag
    if (!product.tags.find((tag) => tag === 'has_active_category')) {
      return;
    }

    // Must be in location's product_ids list
    if (!filteredProductIds.map((x) => parseInt(x)).includes(product.id)) {
      return;
    }

    // Find position tag for sorting
    let tag = searchInArray(product.tags)[0];
    if (!tag) {
      tag = `position__${index}`;
    }

    unorderedProducts[tag] = product;
  });

  // Sort products by their position tags
  const orderedProducts = Object.keys(unorderedProducts)
    .sort()
    .map((key) => unorderedProducts[key]);

  return orderedProducts;
}

/**
 * Search for position tags in product tags array
 * Helper function matching page-map-v4.ts lines 1128-1136
 */
function searchInArray(arr: string[]): string[] {
  const result: string[] = [];
  for (const tag of arr) {
    if (tag.match(/position_\d/)) {
      result.push(tag);
    }
  }
  return result;
}
